/* global Swiper */

/**
 * SliderMarquee
 *
 * Handles marquee/slider for desktop and mobile.
 *
 * DOM elements:
 * - .js-slider-marquee-parent       – container for all scrollers (desktop & mobile Swiper.js)
 * - .js-slider-marquee-items        – marquee item list, cloned for looping effect (desktop)
 * - .js-slider-marquee-item         – single marquee item with aria-label and tabindex
 * - .js-slider-marquee-stop-desktop – stop/start button for desktop animation (toggles is-active, aria-pressed, aria-label)
 * - .js-slider-marquee-prev-desktop – move marquee left (desktop)
 * - .js-slider-marquee-next-desktop – move marquee right (desktop)
 * - .js-slider-marquee-stop-mobile  – stop/start autoplay button for Swiper.js (mobile)
 * - .js-slider-marquee-prev-mobile  – Swiper.js navigation button (previous slide)
 * - .js-slider-marquee-next-mobile  – Swiper.js navigation button (next slide)
 *
 * Options (pass as second argument to constructor):
 * {
 * desktop: {
 * speed: number,                 // Speed value for marquee movement (default: 2)
 * moveAmount: number             // Amount of px to move on navigation (default: 200)
 * },
 * mobile: {
 * slidesPerView: number,         // Number of slides visible at once (default: 1)
 * delay: number,                 // Autoplay delay in ms (default: 4000)
 * prevSlideMessage: string,      // A11y message for previous slide button (default: 'Poprzedni slajd')
 * nextSlideMessage: string       // A11y message for next slide button (default: 'Następny slajd')
 * }
 * }
 *
 * Extending / Customization:
 * You can extend SliderMarquee and override its methods to customize behavior
 * without modifying the original class. Example:
 *
 * import { SliderMarquee } from '../../js/front/components/slider-marquee';
 *
 * class CustomSliderMarquee extends SliderMarquee {
 * createSliderMobile() {
 * custom mobile slider logic
 * }
 * }
 *
 * new CustomSliderMarquee(block);
 */
class SliderMarquee {

	/**
	 * Debounces a function by postponing its execution until a specified time
	 * has elapsed after the last call.
	 *
	 * @param {function} func The function to debounce.
	 * @param {number} wait The debounce delay in milliseconds.
	 * @return {function} Debounced callback.
	 */
	debounce( func, wait ) {

		let timeout;

		return ( ...args ) => {

			clearTimeout( timeout );
			timeout = setTimeout( () => func.apply( this, args ), wait );

		};

	}

	/**
	 * Initializes a new instance of Marquee class.
	 *
	 * @param {Element} block - The parent block element that contains the marquee content.
	 * @param {Object} [options={}] - Optional configuration object for mobile slider.
	 * @param {boolean} [alwaysClone=false] - Determines whether the content cloning is always enforced.
	 * @return {void}
	 */
	constructor( block, options = {}, alwaysClone = false ) {

		this.block = block;
		this.options = options;
		this.alwaysClone = alwaysClone;

		this.clone = false;
		this.canRun = false;

		this.scrollersParent = this.block.querySelector( '.js-slider-marquee-parent' );

		if ( !this.scrollersParent ) {

			return;

		}

		if ( this.scrollersParent.classList.contains( 'js-slider-marquee-init' ) ) {

			return;

		}
		this.scrollersParent.classList.add( 'js-slider-marquee-init' );

		this.scrollers = this.block.querySelectorAll( '.js-slider-marquee-items' );

		this.navStopMobile = this.block.querySelector( '.js-slider-marquee-stop-mobile' );
		this.navPrevMobile = this.block.querySelector( '.js-slider-marquee-prev-mobile' );
		this.navNextMobile = this.block.querySelector( '.js-slider-marquee-next-mobile' );

		this.navStopDesktop = this.block.querySelector( '.js-slider-marquee-stop-desktop' );
		this.navPrevDesktop = this.block.querySelector( '.js-slider-marquee-prev-desktop' );
		this.navNextDesktop = this.block.querySelector( '.js-slider-marquee-next-desktop' );

		this.desktopMediaQuery = window.matchMedia( '(min-width: 768px)' );


		this.swiperInstance = null;
		this.isPausedDesktop = window.globalAnimationPause === true;

		this.cleanupMarqueeDesktop = null;
		this.boundUpdateTabIndexDesktop = this.updateTabIndexDesktop.bind( this );
		this.desktopEventListenersAttached = false;

		this.initBefore();

		this.refreshSliderState = this.debounce( () => {

			if ( this.desktopMediaQuery.matches ) {

				this.refreshDesktopSliderState();

				return;

			}

			this.refreshMobileSliderState();

		}, 200 );

		this.watchPendingImages();
		this.watchLayoutReady();

		if ( this.desktopMediaQuery.matches ) {

			this.refreshDesktopSliderState();

		} else {

			this.refreshMobileSliderState();

		}

		this.desktopMediaQuery.addEventListener( 'change', this.refreshSliderState );
		window.addEventListener( 'resize', this.refreshSliderState );

		this.initAfter();

		this.listenerSlider();

	}

	/**
	 * Initializes any necessary configurations or state before the main logic of the application or component is executed.
	 * This method is typically called early in the lifecycle to set up prerequisites.
	 *
	 * @return {string} An empty string indicating the completion of the initialization process.
	 */
	initBefore(){

		return '';

	}

	/**
	 * A method to handle initialization tasks that need to be performed after
	 * a main setup or configuration process has been completed.
	 *
	 * @return {string} An empty string indicating the completion of the initialization process.
	 */
	initAfter(){

		return '';

	}

	/**
	 * Creates and appends a slider element after a specified HTML element in the DOM.
	 * The slider is typically a UI component for selecting a value from a range of values.
	 *
	 * @return {string} An empty string indicating the completion of the initialization process.
	 */
	createSliderAfter(){

		return '';

	}

	/**
	 * Builds an aria-label string for a nav button, appending the button's
	 * data-nav-target value (if present) to the base label.
	 *
	 * @param {Element} btn       - The button element to read data-nav-target from.
	 * @param {string}  baseLabel - The base label text (e.g. 'Odtwórz').
	 * @return {string} The final aria-label string.
	 */
	getNavAriaLabel( btn, baseLabel ) {

		const navTarget = btn && btn.dataset.navTarget;

		return navTarget ? `${baseLabel} ${navTarget}` : baseLabel;

	}


	/**
	 * Attaches an event listener to a slider component and handles its interactions.
	 * This method is used to capture events triggered on a slider, enabling the implementation
	 * of specific behaviors or actions based on user interaction.
	 *
	 * @return {string} A string result, typically representing the processed output or a status value.
	 */
	listenerSlider(){

		window.addEventListener( 'globalAnimationPause', ( e ) => {

			const value = e.detail.value;

			if ( this.desktopMediaQuery.matches ) {

				if ( !value ) {

					this.navStopDesktop.classList.remove( 'is-active' );
					this.navStopDesktop.setAttribute( 'aria-pressed', 'false' );
					this.navStopDesktop.setAttribute( 'aria-label', this.getNavAriaLabel( this.navStopDesktop, 'Zatrzymaj odtwarzanie' ) );


				} else {

					this.navStopDesktop.classList.add( 'is-active' );
					this.navStopDesktop.setAttribute( 'aria-pressed', 'true' );
					this.navStopDesktop.setAttribute( 'aria-label', this.getNavAriaLabel( this.navStopDesktop, 'Odtwórz' ) );

				}

			} else if ( this.swiperInstance?.autoplay ) {

				if ( !value ) {

					this.swiperInstance.autoplay.start();
					this.navStopMobile.classList.remove( 'is-active' );
					this.navStopMobile.setAttribute( 'aria-pressed', 'false' );
					this.navStopMobile.setAttribute( 'aria-label', this.getNavAriaLabel( this.navStopMobile, 'Zatrzymaj odtwarzanie' ) );

				} else {

					this.swiperInstance.autoplay.stop();
					this.navStopMobile.classList.add( 'is-active' );
					this.navStopMobile.setAttribute( 'aria-pressed', 'true' );
					this.navStopMobile.setAttribute( 'aria-label', this.getNavAriaLabel( this.navStopMobile, 'Odtwórz' ) );

				}

			}

		} );

		return '';

	}

	/**
	 * Checks the state of the slider and performs necessary updates based on conditions.
	 * Handles destruction of a swiper instance for mobile mode, clears animation styles for desktop,
	 * and updates class states based on the slider's dimensions.
	 *
	 * @return {boolean} Returns `true` if the slider passes all checks and updates; `false` otherwise.
	 */
	checkSlider(){

		// Check exist

		const original = this.block.querySelector( '.js-slider-marquee-items' );

		if ( !original ) {

			return false;

		}

		// Mobile, destroy swiper

		if ( this.swiperInstance ) {

			this.swiperInstance.destroy( true, true );
			this.swiperInstance = null;

		}

		// Desktop, clear style

		this.scrollers.forEach( m => {

			m.style.animation = "";

		} );

		// Data, measure natural widths (drop widths frozen by a previous desktop run)

		this.resetItemWidths();

		const cleanOriginal = this.block.querySelector( '.js-slider-marquee-items' );

		let originalWidth = 0;
		const items = cleanOriginal.querySelectorAll( '.js-slider-marquee-item' );

		items.forEach( ( item ) => {

			originalWidth += this.getItemOuterWidth( item );

		} );

		if ( originalWidth === 0 ) {

			originalWidth = cleanOriginal.clientWidth;

		}

		// Desktop, Add class static

		this.scrollersParent.classList.remove( 'is-marquee-static' );

		if( this.desktopMediaQuery.matches ){

			// clientWidth is rounded to an integer, items are fractional - keep a 1px tolerance

			if ( originalWidth <= this.getScrollerAvailableWidth() + 1 ) {

				this.removeClonedContent();

				this.scrollersParent.classList.add( 'is-marquee-static' );

				return false;

			}

		}

		return true;

	}

	/**
	 * Removes cloned marquee content so the static desktop state does not render
	 * duplicated items after a resize recalculation.
	 *
	 * @return {void}
	 */
	removeClonedContent() {

		if ( this.clone !== true ) {

			return;

		}

		const scrollers = Array.from( this.block.querySelectorAll( '.js-slider-marquee-items' ) );

		scrollers.slice( 1 ).forEach( ( scroller ) => {

			scroller.remove();

		} );

		this.scrollers = this.block.querySelectorAll( '.js-slider-marquee-items' );
		this.clone = false;

	}

	/**
	 * Removes inline widths set by the desktop marquee so items can be measured
	 * at their natural size.
	 *
	 * @return {void}
	 */
	resetItemWidths() {

		this.block.querySelectorAll( '.js-slider-marquee-item' ).forEach( ( item ) => {

			item.style.width = '';

		} );

	}

	/**
	 * Returns the width to assign to `style.width` so the item keeps its current
	 * rendered size. Subtracts padding and border for content-box items.
	 *
	 * @param {Element} item The marquee item element.
	 * @return {number} Width for the inline style.
	 */
	getItemStyleWidth( item ) {

		const width = this.getItemWidth( item );
		const computedStyle = window.getComputedStyle( item );

		if ( computedStyle.boxSizing === 'border-box' ) {

			return width;

		}

		const extra = [ 'paddingLeft', 'paddingRight', 'borderLeftWidth', 'borderRightWidth' ]
			.reduce( ( sum, prop ) => sum + ( parseFloat( computedStyle[prop] ) || 0 ), 0 );

		return Math.max( width - extra, 0 );

	}

	/**
	 * Returns the rendered width of a marquee item without margins.
	 *
	 * @param {Element} item The marquee item element.
	 * @return {number} Rendered item width.
	 */
	getItemWidth( item ) {

		if ( !item ) {

			return 0;

		}

		const rect = item.getBoundingClientRect();

		return rect.width || item.offsetWidth || 0;

	}

	/**
	 * Returns the rendered width of a marquee item including horizontal margins.
	 *
	 * @param {Element} item The marquee item element.
	 * @return {number} Outer item width.
	 */
	getItemOuterWidth( item ) {

		if ( !item ) {

			return 0;

		}

		const computedStyle = window.getComputedStyle( item );
		const marginLeft = parseFloat( computedStyle.marginLeft ) || 0;
		const marginRight = parseFloat( computedStyle.marginRight ) || 0;

		return this.getItemWidth( item ) + marginLeft + marginRight;

	}

	/**
	 * Returns the horizontal space available for marquee items inside the parent,
	 * excluding the parent's horizontal padding.
	 *
	 * @return {number} Available inner width.
	 */
	getScrollerAvailableWidth() {

		if ( !this.scrollersParent ) {

			return 0;

		}

		const computedStyle = window.getComputedStyle( this.scrollersParent );
		const paddingLeft = parseFloat( computedStyle.paddingLeft ) || 0;
		const paddingRight = parseFloat( computedStyle.paddingRight ) || 0;

		return this.scrollersParent.getBoundingClientRect().width - paddingLeft - paddingRight;

	}

	/**
	 * Runs the full desktop marquee recalculation flow.
	 *
	 * @return {void}
	 */
	refreshDesktopSliderState() {

		this.canRun = this.checkSlider();

		if ( this.canRun ) {

			this.cloneContent();
			this.createSlider();

		}

		this.updateSliderNav();

	}

	/**
	 * Refreshes the mobile slider state without destroying the existing Swiper
	 * instance on ordinary mobile resizes.
	 *
	 * @return {void}
	 */
	refreshMobileSliderState() {

		this.removeClonedContent();
		this.scrollersParent.classList.remove( 'is-marquee-static' );

		if ( !this.swiperInstance ) {

			this.createSliderMobile();

		} else {

			this.swiperInstance.update();
			this.updateTabIndexMobile();

		}

		this.updateSliderNav();

	}

	/**
	 * Schedules a width recalculation once the page is fully loaded and web fonts
	 * are ready. The first measurement can run before styles, fonts or lazy
	 * assets settle, which leaves a single short logo looped with navigation.
	 *
	 * @return {void}
	 */
	watchLayoutReady() {

		if ( document.readyState !== 'complete' ) {

			window.addEventListener( 'load', this.refreshSliderState, { once: true } );

		}

		if ( document.fonts && document.fonts.ready ) {

			document.fonts.ready.then( this.refreshSliderState );

		}

	}

	/**
	 * Schedules a width recalculation when images are still loading during the
	 * initial render. This covers external assets that resolve after init.
	 *
	 * @return {void}
	 */
	watchPendingImages() {

		const images = this.block.querySelectorAll( '.js-slider-marquee-items img' );

		images.forEach( ( img ) => {

			if ( img.complete ) {

				return;

			}

			img.addEventListener( 'load', this.refreshSliderState, { once: true } );
			img.addEventListener( 'error', this.refreshSliderState, { once: true } );

		} );

	}

	/**
	 * Clones the content of the slider marquee if specific conditions are met.
	 * The method ensures that the cloning process happens only once and adjusts
	 * accessibility labels for the original items. A clone of the items is added
	 * to support the marquee slider functionality.
	 *
	 * @return {void} Does not return a value.
	 */
	cloneContent(){

		if( this.clone === true ){

			return;

		}


		const original = this.block.querySelector( '.js-slider-marquee-items' );

		if ( !original ) {

			return;

		}

		if ( this.desktopMediaQuery.matches ) {

			// Clone

			const clone = original.cloneNode( true );

			const originalLi = Array.from( this.scrollers[0].querySelectorAll( '.js-slider-marquee-item' ) );


			originalLi.forEach( ( li, i ) => {

				li.setAttribute( 'aria-label', `${i + 1} / ${originalLi.length}` );

			} );

			clone.setAttribute( 'aria-hidden', 'true' );

			original.parentNode.insertBefore( clone, original.nextSibling );

			this.scrollers = this.block.querySelectorAll( '.js-slider-marquee-items' );

			this.clone = true;

		}

	}


	/**
	 * Creates a Swiper instance based on the given scrollers and animation speed.
	 *
	 * @return {void}
	 */
	createSlider() {

		/**
		 * Toggles the Swiper instance based on the given media query.
		 */

		if ( this.desktopMediaQuery.matches ) {

			/**
			 * Desktop version of the slider,
			 * custom WCAG-compliant code, scrolling based on requestAnimationFrame
			 * > 768px
			 */

			this.createSliderDesktop();
			this.block.removeEventListener( 'keydown', this.boundUpdateTabIndexDesktop );
			this.block.addEventListener( 'keydown', this.boundUpdateTabIndexDesktop );

		} else {

			/**
			 * Mobile version of the slider, based on Swiper.js
			 * < 768px
			 */

			if ( this.navStopDesktop ) {

				this.navStopDesktop.removeAttribute( 'style' );

			}

			if ( this.navPrevDesktop ) {

				this.navPrevDesktop.removeAttribute( 'style' );

			}

			if ( this.navNextDesktop ) {

				this.navNextDesktop.removeAttribute( 'style' );

			}

			this.block.removeEventListener( 'keydown', this.boundUpdateTabIndexDesktop );

			this.createSliderMobile();

		}

	}

	/**
	 * Desktop Slider
	 *
	 * Initializes and manages the desktop version of the slider with a custom WCAG-compliant scrolling logic
	 * based on requestAnimationFrame for screens larger than 768px. This method sets up the marquee animations,
	 * handles user interactions (e.g., button clicks, keyboard events), recalculates dimensions on resize,
	 * and ensures correct behavior through IntersectionObserver.
	 *
	 * @return {void} This function has no return value.
	 */
	createSliderDesktop(){

		if ( this.swiperInstance ) {

			this.swiperInstance.destroy( true, true );

			this.swiperInstance = null;

		}

		if ( !this.scrollers || this.scrollers.length < 2 ) {

			return;

		}


		/**
		 * Adjusts the marquee content by applying an animation to all scrollers.
		 * The animation scrolls the content continuously in a linear direction.
		 *
		 * The function sets a predefined duration for the scrolling animation and applies it
		 * to the styles of each element in the scrollers array.
		 * The animation scrolls infinitely in a linear manner.
		 */
		const adjustMarqueeContent = () => {

			const speed = this.options.desktop && this.options.desktop.speed
				? this.options.desktop.speed
				: 60;

			const first = this.scrollers[0];
			if ( !first ) {

				return;

			}

			const second = this.scrollers[1];
			if ( !second ) {

				return;

			}

			const firstItems = first.querySelectorAll( '.js-slider-marquee-item' );
			const secondItems = second.querySelectorAll( '.js-slider-marquee-item' );

			let totalWidth = 0;
			firstItems.forEach( ( item, index ) => {

				const width = this.getItemStyleWidth( item );

				if ( width > 0 ) {

					item.style.width = `${width}px`;

					if ( secondItems[index] ) {

						secondItems[index].style.width = `${width}px`;

					}

				}

				totalWidth += this.getItemOuterWidth( item );

			} );



			this.scrollers.forEach( scroller => {

				const duration = totalWidth / speed;

				scroller.style.animation = `marquee_scroll ${duration}s linear infinite`;
				scroller.style.animationPlayState = 'paused';

			} );

			this.scrollers.forEach( scroller => {

				scroller.style.animationPlayState = this.isPausedDesktop ? 'paused' : 'running';

			} );

		}

		// First calculation
		adjustMarqueeContent();

		// Wait for all images to load, then recalculate
		const first = this.scrollers[0];
		if ( first ) {

			const allImages = Array.from( first.querySelectorAll( 'img' ) );
			const imagePromises = allImages.map( img => {

				if ( img.complete ) {

					return Promise.resolve();

				}
				return new Promise( ( resolve ) => {

					img.addEventListener( 'load', resolve );
					img.addEventListener( 'error', resolve );

				} );

			} );

			Promise.all( imagePromises ).then( () => {

				// Reset widths before recalculation
				const firstItems = first.querySelectorAll( '.js-slider-marquee-item' );
				const second = this.scrollers[1];
				const secondItems = second ? second.querySelectorAll( '.js-slider-marquee-item' ) : [];

				firstItems.forEach( ( item, index ) => {

					item.style.width = '';
					if ( secondItems[index] ) {

						secondItems[index].style.width = '';

					}

				} );

				// Recalculate after images loaded
				adjustMarqueeContent();

			} );

		}

		/**
		 *
		 */
		const togglePlayPause = () => {

			this.isPausedDesktop = !this.isPausedDesktop;
			this.scrollers.forEach( m => {

				m.style.animationPlayState = this.isPausedDesktop ? 'paused' : 'running';

			} );

		}

		/**
		 * Handles the shifting of list items from the first position in one list to the end of the next list.
		 * This function iterates through all `scrollers`, extracts the first `<li>` element in each list,
		 * and moves it to the end of the next list in a circular manner.
		 *
		 * The last item's first `<li>` is moved to the first list's end.
		 */
		const onLeft = () => {

			for ( let i = 0; i < this.scrollers.length; i++ ) {

				const firstLi = this.scrollers[i].querySelector( '.js-slider-marquee-item:first-child' );
				const nextUl = this.scrollers[( i + 1 ) % this.scrollers.length];

				if ( firstLi ) {

					nextUl.appendChild( firstLi );

				}

			}

		}

		/**
		 * A function that redistributes the last list item (li) from each scroller
		 * element to the beginning of the previous scroller in a circular fashion.
		 * This operation is performed on all scroller elements in reverse order.
		 */
		const onRight = () => {

			for ( let i = this.scrollers.length - 1; i >= 0; i-- ) {

				const lastLi = this.scrollers[i].querySelector( '.js-slider-marquee-item:last-child' );
				const prevUl = this.scrollers[( i - 1 + this.scrollers.length ) % this.scrollers.length];
				if ( lastLi ) {

					prevUl.insertBefore( lastLi, prevUl.firstChild );

				}

			}

		}

		/**
		 * Left / Right slider
		 */

		if ( !this.desktopEventListenersAttached ) {

			const leftBtn = this.block.querySelector( '.js-slider-marquee-prev-desktop' );
			if ( leftBtn ) {

				leftBtn.addEventListener( 'click', () => {

					const wasPaused = this.isPausedDesktop;
					onLeft();
					this.scrollers.forEach( m => {

						m.style.animation = "";

					} );

					setTimeout( () => {

						adjustMarqueeContent();
						if ( wasPaused ) {

							this.scrollers.forEach( m => {

								m.style.animationPlayState = 'paused';

							} );

						}

					}, 10 );

				} );

			}

			const rightBtn = this.block.querySelector( '.js-slider-marquee-next-desktop' );

			if ( rightBtn ) {

				rightBtn.addEventListener( 'click', () => {

					const wasPaused = this.isPausedDesktop;
					onRight();
					this.scrollers.forEach( m => {

						m.style.animation = "";

					} );

					setTimeout( () => {

						adjustMarqueeContent();
						if ( wasPaused ) {

							this.scrollers.forEach( m => {

								m.style.animationPlayState = 'paused';

							} );

						}

					}, 10 );

				} );

			}


			/**
			 * Stop slider
			 */

			const stopBtn = this.block.querySelector( '.js-slider-marquee-stop-desktop' );
			if ( stopBtn ) {

				stopBtn.addEventListener( 'click', () => {

					togglePlayPause();

					if ( this.isPausedDesktop ) {

						stopBtn.classList.add( 'is-active' );
						stopBtn.setAttribute( 'aria-pressed', 'true' );
						stopBtn.setAttribute( 'aria-label', this.getNavAriaLabel( stopBtn, 'Odtwórz' ) );

					} else {

						stopBtn.classList.remove( 'is-active' );
						stopBtn.setAttribute( 'aria-pressed', 'false' );
						stopBtn.setAttribute( 'aria-label', this.getNavAriaLabel( stopBtn, 'Zatrzymaj odtwarzanie' ) );

					}

				} );

			}

			this.desktopEventListenersAttached = true;

		}

		/**
		 * Cleans up and removes the desktop marquee element from the DOM.
		 */

		this.cleanupMarqueeDesktop = () => {

			this.scrollers.forEach( m => {

				m.style.animation = "";

			} );


		};

	}

	/**
	 * Initializes and configures a mobile version of the slider using Swiper.js.
	 * This function sets up the slider for screens smaller than 768px, with specific
	 * behaviors for autoplay, navigation, and accessibility. It also cleans up any
	 * existing desktop slider setup if present.
	 *
	 * @return {void} This method does not return a value.
	 */
	createSliderMobile(){

		if ( this.cleanupMarqueeDesktop ) {

			this.cleanupMarqueeDesktop();
			this.cleanupMarqueeDesktop = null;

		}

		if ( !this.swiperInstance ) {

			const delay = this.options.mobile && this.options.mobile.delay !== undefined ? this.options.mobile.delay : 4000;
			const prevSlideMessage = this.options.mobile && this.options.mobile.prevSlideMessage ? this.options.mobile.prevSlideMessage : 'Poprzedni slajd';
			const nextSlideMessage = this.options.mobile && this.options.mobile.nextSlideMessage ? this.options.mobile.nextSlideMessage : 'Następny slajd';
			const spaceBetween = this.options && this.options.mobile && this.options.mobile.slidesPerView ? this.options.mobile.slidesPerView : 1;
			const breakpoints= this.options && this.options.mobile && this.options.mobile.breakpoints ? this.options.mobile.breakpoints : {};

			this.swiperInstance = new Swiper( this.scrollersParent, {
				direction: 'horizontal',
				spaceBetween: 0,
				slidesPerView: spaceBetween,
				loop: true,
				watchSlidesProgress: true,
				slideToClickedSlide: true,
				autoplay: {
					delay: delay,
					disableOnInteraction: false
				},
				breakpoints: breakpoints,
				keyboard: {
					enabled: true,
				},
				navigation: {
					prevEl: this.block.querySelector( '.js-slider-marquee-prev-mobile' ),
					nextEl: this.block.querySelector( '.js-slider-marquee-next-mobile' ),
				},
				a11y: {
					enabled: true,
					prevSlideMessage: prevSlideMessage,
					nextSlideMessage: nextSlideMessage,
				},
				on: {
					slideChange: () => {

						this.updateTabIndexMobile();

					},
					afterInit: () => {

						setTimeout( () => {

							if ( this.swiperInstance ) {

								this.swiperInstance.slideToLoop( 0, 0 );
								this.swiperInstance.update();

							}

						}, 1000 );

					},
					init: () => {

						this.updateTabIndexMobile();

					},
					resize: () => {

						this.updateTabIndexMobile();

					},
				}
			} );

			if( window.globalAnimationPause === true ) {

				this.swiperInstance.autoplay.stop();

			}

			const stopBtnMobile = this.block.querySelector( '.js-slider-marquee-stop-mobile' );

			if ( stopBtnMobile ) {

				stopBtnMobile.addEventListener( 'click', () => {

					if ( window.globalAnimationPause === true ) {

						return;

					}

					const isRunning = this.swiperInstance?.autoplay?.running;

					if ( isRunning ) {

						this.swiperInstance.autoplay.stop();
						stopBtnMobile.classList.add( 'is-active' );
						stopBtnMobile.setAttribute( 'aria-pressed', 'true' );
						stopBtnMobile.setAttribute( 'aria-label', this.getNavAriaLabel( stopBtnMobile, 'Odtwórz' ) );

					} else {

						this.swiperInstance.autoplay.start();
						stopBtnMobile.classList.remove( 'is-active' );
						stopBtnMobile.setAttribute( 'aria-pressed', 'false' );
						stopBtnMobile.setAttribute( 'aria-label', this.getNavAriaLabel( stopBtnMobile, 'Zatrzymaj odtwarzanie' ) );

					}

				} );

			}

		}

	}

	/**
	 * Updates the slider navigation display based on the current screen size, state of the slider,
	 * and other related conditions.
	 *
	 * This function determines the visibility of navigation elements depending on whether the slider
	 * is being viewed on a desktop or a mobile device. It also ensures the slider instance is updated
	 * in mobile view when necessary.
	 */
	updateSliderNav = () => {

		/**
		 * Updates the display style of a specified element.
		 *
		 * @param {HTMLElement} el - The HTML element whose display property is being modified.
		 * @param {string} value - The value to set for the display property (e.g., "block", "none").
		 */
		const setDisplay = ( el, value ) => {

			if ( el ) {

				el.style.display = value;

			}

		};

		/**
		 * Toggles a desktop navigation state class for CTA slider blocks.
		 *
		 * @param {boolean} isHidden Whether desktop navigation is hidden.
		 * @return {void}
		 */
		const setDesktopNavHiddenState = ( isHidden ) => {

			if ( this.block && this.block.classList.contains( 'b-cta-slider' ) ) {

				this.block.classList.toggle( 'b-cta-slider--desktop-nav-hidden', isHidden );

			}

		};

		if ( this.desktopMediaQuery.matches ) {

			setDisplay( this.navStopMobile, 'none' );

			const display = this.canRun ? 'flex' : 'none';
			setDisplay( this.navStopDesktop, display );
			setDisplay( this.navPrevDesktop, display );
			setDisplay( this.navNextDesktop, display );
			setDesktopNavHiddenState( ! this.canRun );

		} else {

			if ( this.swiperInstance ) {

				this.swiperInstance.update();

			}

			setDisplay( this.navStopDesktop, 'none' );
			setDisplay( this.navPrevDesktop, 'none' );
			setDisplay( this.navNextDesktop, 'none' );
			setDesktopNavHiddenState( false );

			const display = this.swiperInstance?.isLocked ? 'none' : 'flex';
			setDisplay( this.navStopMobile, display );

		}

	}

	/**
	 * Updates the `tabindex` attribute of each slide in the swiper instance.
	 *
	 * @return {void}
	 */
	updateTabIndexMobile = () => {

		if ( !this.swiperInstance ) {

			return;

		}

		this.swiperInstance.slides.forEach( ( slide ) => {

			slide.setAttribute( 'tabindex', '-1' );

			if ( slide.classList.contains( 'swiper-slide-visible' ) ) {

				slide.querySelectorAll( '[tabindex="-1"]' ).forEach( ( el ) => {

					el.removeAttribute( 'tabindex' );

				} );

			} else {

				slide.querySelectorAll( 'a, button, input, textarea, select, [tabindex]' )
					.forEach( ( el ) => {

						el.setAttribute( 'tabindex', '-1' );

					} );

			}

		} );

	};

	/**
	 * Updates the visible marquee items by adjusting the `tabindex` and `inert`
	 * attributes of links based on their visibility within the parent container.
	 * Runs on Tab and VoiceOver (Control+Option+Arrow) navigation.
	 *
	 * @param {Event} e The keydown event object.
	 * @return {void}
	 */
	updateTabIndexDesktop( e ) {

		const isTab = e.key === 'Tab';
		const isVoiceOver = e.ctrlKey && e.altKey && (
			e.key === 'ArrowRight' ||
			e.key === 'ArrowLeft' ||
			e.key === 'ArrowUp' ||
			e.key === 'ArrowDown'
		);

		if ( !isTab && !isVoiceOver ) {

			return;

		}

		const parentRect = this.block.querySelector( '.js-slider-marquee-parent' ).getBoundingClientRect();

		this.scrollers.forEach( container => {

			if ( container.getAttribute( 'aria-hidden' ) === 'true' ) {

				return;

			}

			const items = container.querySelectorAll( '.js-slider-marquee-item' );
			items.forEach( li => {

				const link = li.querySelector( 'a' );

				if ( !link ) {

					return;

				}

				const liRect = li.getBoundingClientRect();

				if ( liRect.right > parentRect.left && liRect.left < parentRect.right ) {

					li.removeAttribute( 'inert' );
					link.setAttribute( 'tabindex', '0' );

				} else {

					li.setAttribute( 'inert', '' );
					link.setAttribute( 'tabindex', '-1' );

				}

			} );

		} );

	}

}

export { SliderMarquee };
